import { AfterViewInit, ChangeDetectionStrategy, Component, forwardRef, ViewChild } from "@angular/core";
import { FormQuestion, NumberQuestion } from "../../../checklist.interface";
import { QuestionComponent } from "../question.component";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, Validators } from "@angular/forms";
import { IonInput, IonModal, ModalController } from "@ionic/angular";
import { minDecimalPlacesValidator, maxDecimalPlacesValidator } from "../../shared/validators/decimal‐places.validator";
import { MeasurementService, Unit } from "source/app/configuration/services/measurement.service";

@Component({
	selector: "app-number-question",
	templateUrl: "./number-question.component.html",
	styleUrls: ["./number-question.component.scss"],
	changeDetection: ChangeDetectionStrategy.Default,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => NumberQuestionComponent),
			multi: true,
		},
	],
})
export class NumberQuestionComponent
	extends QuestionComponent<NumberQuestion>
	implements ControlValueAccessor, AfterViewInit
{
	constructor(
		private modalController: ModalController,
		private measurementService: MeasurementService,
	) {
		super();
		this.numberFormControl = new FormControl(null, [
			Validators.required,
			Validators.pattern("[0-9]+([.,][0-9]+)?"),
		]);
	}

	@ViewChild("modal") modal!: IonModal;
	@ViewChild("number") numberInput!: IonInput;

	protected onModalDidPresent = () => {
		this.numberInput.setFocus();
	};

	protected onModalDismiss = (event: CustomEvent) => {
		const dismissalReason = event.detail.role;
		if (dismissalReason === "backdrop") {
			this.resetValueAndSetPristine();
		}
	};

	private resetValueAndSetPristine = () => {
		const inputValue = this.answer?.answers.length == 0 ? null : this.answer?.answers[0];
		this.numberFormControl.setValue(inputValue);
		this.numberFormControl.markAsPristine();
	};

	protected numberFormControl: FormControl;
	protected unit: Unit;

	protected accept = async (): Promise<void> => {
		if (this.numberFormControl.valid) {
			const answer: string = <string>this.numberFormControl.value.toString();
			await this.setValueCloseAndReset(answer);
		}
	};

	protected discard = async (): Promise<void> => {
		this.numberFormControl.setValue(null);
		await this.setValueCloseAndReset(undefined);
	};

	private setValueCloseAndReset = async (answer: string | undefined): Promise<void> => {
		this.setAnswers(answer);
		await this.modalController.dismiss(); //we need to await this, so we dont change the UI while it closes
		this.numberFormControl.markAsPristine();
	};

	override writeValue(answer: FormQuestion | undefined): void {
		super.writeValue(answer);
		const inputValue = answer?.answers.length == 0 ? null : answer?.answers[0];
		this.numberFormControl.setValue(inputValue);
	}

	async ngAfterViewInit(): Promise<void> {
		if (this.question.decimalScale?.minimum > 0) {
			this.numberFormControl.addValidators(minDecimalPlacesValidator(this.question.decimalScale.minimum));
		}
		if (this.question.decimalScale?.maximum > -1) {
			this.numberFormControl.addValidators(maxDecimalPlacesValidator(this.question.decimalScale.maximum));
		}

		if (this.question.minimum != null) {
			this.numberFormControl.addValidators(Validators.min(this.question.minimum));
		}

		if (this.question.maximum != null) {
			this.numberFormControl.addValidators(Validators.max(this.question.maximum));
		}

		if (this.question.measurementUnit != null) {
			this.unit = await this.measurementService.getUnitById(this.question.measurementUnit);
		}
	}
}
